import {
  createContext,
  FC,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {
  useGetWarehouseKitRequestData,
  WarehouseKitRequestData,
} from 'views/warehouse/useGetWarehouseKitRequestData';

interface WarehouseKitRequestProviderProps {
  fetchKitRequestLines?: boolean;
  includeStartedFilter?: boolean;
}

type WarehouseKitRequestContextType = WarehouseKitRequestData & {
  currentPage: number;
  totalPages: number;
  totalKitRequests: number;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  setPage: (page: number) => void;
  searchTerm: string;
  debouncedSearchTerm: string;
  setSearchTerm: (term: string) => void;
};

const WarehouseKitRequestContext =
  createContext<WarehouseKitRequestContextType | null>(null);

export const WarehouseKitRequestProvider: FC<
  WarehouseKitRequestProviderProps
> = ({
  children,
  fetchKitRequestLines = false,
  includeStartedFilter = true,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      if (searchTerm !== debouncedSearchTerm) {
        setCurrentPage(1);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, debouncedSearchTerm]);

  const hookResult = useGetWarehouseKitRequestData({
    fetchKitRequestLines,
    pageNumber: fetchKitRequestLines ? currentPage : undefined,
    search: fetchKitRequestLines ? debouncedSearchTerm : undefined,
    includeStartedFilter,
  });

  const totalPages = hookResult.totalPages ?? 1;
  const totalKitRequests =
    hookResult.totalCount ?? hookResult.kitRequests.length;

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  }, [totalPages]);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  }, []);

  const setPage = useCallback(
    (page: number) => {
      setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    },
    [totalPages]
  );

  const contextValue = {
    ...hookResult,
    currentPage,
    totalPages,
    totalKitRequests,
    goToNextPage,
    goToPreviousPage,
    setPage,
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm,
  };

  return (
    <WarehouseKitRequestContext.Provider value={contextValue}>
      {children}
    </WarehouseKitRequestContext.Provider>
  );
};

export const useWarehouseKitRequest = () => {
  const context = useContext(WarehouseKitRequestContext);

  if (!context) {
    throw new Error(
      'useWarehouseKitRequest must be used within a WarehouseKitRequestProvider'
    );
  }

  return context;
};
